import { FilterConstants } from '../../shared/constants/filter-constants';
import { MenuModel } from '../../data/model/MenuModel/menu.model';
import { routeRoot } from './nav-tabs';

/* Shared by the sidebar (NavDrawerComponent) and the topbar search, so both cluster and
   navigate the backend menu the same way. */

/* Visual-only grouping of the real backend menu (dataManager.menuArray stays a flat,
   permission-filtered list — this only clusters it under section labels, mirroring
   design/prototype/assets/shell.js GROUPS). A name the map doesn't recognise still
   renders, just under a trailing "More" group, so a new backend menu item never
   silently disappears. */
export const NAV_GROUPS: { label: string; names: string[] }[] = [
  { label: '', names: ['home'] },
  { label: 'Payments', names: ['customers', 'subscriptions', 'transactions', 'payments', 'digital wallet', 'deposits', 'statements', 'all merchants', 'ach returns'] },
  { label: 'Risk', names: ['disputes', 'vamp details', 'risk management'] },
  { label: 'Reports', names: ['reports', 'ai reports'] },
  { label: 'Account', names: ['support tickets', 'payarc assist', 'api', 'events & logs', 'merchant profile'] },
  { label: 'Developer', names: ['bridge'] },
  { label: 'Support', names: ['zendesk tickets', 'knowledge base'] }
];

export const NAV_GROUP_FALLBACK = 'More';

/** Section label for a backend menu name, e.g. 'Deposits' → 'Payments'. */
export function navGroupLabel(menuName: string): string {
  const name = (menuName || '').toLowerCase();
  const group = NAV_GROUPS.find(g => g.names.indexOf(name) > -1);
  return group ? group.label : NAV_GROUP_FALLBACK;
}

/**
 * True when the user's backend menu links to the page under this route root (e.g. 'ach-rejects').
 * Widgets that surface a menu page elsewhere (e.g. a Home card) use this to follow the same gate
 * as the sidebar and the topbar search.
 */
export function menuHasPage(menus: MenuModel[], root: string): boolean {
  return (Array.isArray(menus) ? menus : [])
    .some(menu => !!menu && [menu.routes, menu.routerLink].some(route => routeRoot(route) === root));
}

/* Saved list-screen filters. Navigating from the menu clears them so a list opens unfiltered. */
const LIST_FILTER_STORAGE_KEYS: string[] = [
  FilterConstants.SUBSCRIPTION_FILTER_COUNT_STORAGE_KEY,
  FilterConstants.SUBSCRIPTION_FILTER_STORAGE_KEY,
  FilterConstants.PAYOUTS_FILTER_COUNT_STORAGE_KEY,
  FilterConstants.PAYOUTS_FILTER_STORAGE_KEY,
  FilterConstants.PAYMENT_FILTER_COUNT_STORAGE_KEY,
  FilterConstants.PAYMENT_FILTER_STORAGE_KEY,
  FilterConstants.TRANSACTIONS_FILTER_COUNT_STORAGE_KEY,
  FilterConstants.TRANSACTIONS_FILTER_STORAGE_KEY,
  FilterConstants.INVOICE_FILTER_COUNT_STORAGE_KEY,
  FilterConstants.INVOICE_FILTER_STORAGE_KEY,
  FilterConstants.COUPON_FILTER_COUNT_STORAGE_KEY,
  FilterConstants.COUPON_FILTER_STORAGE_KEY,
  FilterConstants.CYCLE_FILTER_COUNT_STORAGE_KEY,
  FilterConstants.CYCLE_FILTER_STORAGE_KEY,
  FilterConstants.DISPUTE_FILTER_COUNT_STORAGE_KEY,
  FilterConstants.DISPUTE_FILTER_STORAGE_KEY,
  FilterConstants.CUSTOMER_FILTER_COUNT_STORAGE_KEY,
  FilterConstants.CUSTOMER_FILTER_STORAGE_KEY,
  FilterConstants.DISPUTE_REPORT_FILTER_STORAGE_KEY,
  FilterConstants.DISPUTE_REPORT_FILTER_COUNT_STORAGE_KEY,
  FilterConstants.REVIEW_FILTER_STORAGE_KEY,
  FilterConstants.REVIEW_FILTER_COUNT_STORAGE_KEY,
  FilterConstants.PAYMENT_INVOICE_FILTER_STORAGE_KEY,
  FilterConstants.PAYMENT_INVOICE_FILTER_COUNT_STORAGE_KEY,
  'customer_data' // BaseComponent.CUSTOMER_DATA
];

/** Removes every saved list filter. Pass the app's 'LOCALSTORAGE' instance; browser only. */
export function clearListFilters(storage: Pick<Storage, 'removeItem'>): void {
  LIST_FILTER_STORAGE_KEYS.forEach(key => storage.removeItem(key));
}
